console.log('✅ utils-convert-number.js loaded successfully!'); // Debugging confirmation

// Utility functions for number conversions

/**
 * Converts a number string from UK format to any language format.
 * @param {string} s - The number string.
 * @param {string} languageCode - The language code.
 * @returns {string} - The formatted number string.
 */
export function convertNumberStringFromUKFormatToAnyLanguageFormatDouble(s, languageCode) {
  const result = getDoubleInBasicFormatFromAnyLanguage(s, languageCode).toString();
  return result;
}

/**
 * Gets a double value from a number string considering the language format.
 * @param {string} s - The number string.
 * @param {string} languageCode - The language code.
 * @returns {number} - The parsed double value.
 */

/**
 * Gets a double value from a number string considering the language format.
 * @param {string} s - The number string.
 * @param {string} languageCode - The language code.
 * @returns {number} - The parsed double value.
 */
function getDoubleInBasicFormatFromAnyLanguage(s, languageCode) {
  let result = 0.0;
  let isValid = true;

  // Ensure s is a string and handle null or undefined gracefully
  s = s !== null && s !== undefined ? String(s) : '';

  // Check if input is empty after conversion
  if (!s.trim()) {
    return result;
  }

  // Remove spaces and trim the string
  s = s.replace(/\s+/g, '').trim();

  // Languages that use dot as thousands separator and comma as decimal separator
  const languagesWithDotAsThousandsSeparator = ['fr', 'es', 'de', 'pt', 'tr', 'it'];
  // Languages that primarily use dot as decimal separator
  const languagesWithDotAsDecimalSeparator = ['en', 'us', 'zh', 'ja', 'ko', 'ar'];

  // Check the formatting based on the language code
  if (languagesWithDotAsThousandsSeparator.includes(languageCode)) {
    // Replace thousands separator (.) with empty string
    s = s.replace(/\./g, '');
    // Replace decimal separator (,) with dot (.)
    s = s.replace(/,/g, '.');

    // Check for invalid characters
    for (const c of s) {
      if ((c < '0' || c > '9') && c !== '.') {
        isValid = false;
        break;
      }
    }
  } else if (languagesWithDotAsDecimalSeparator.includes(languageCode)) {
    // Remove commas for decimal-separated languages
    s = s.replace(/,/g, '');

    // Check for invalid characters
    for (const c of s) {
      if ((c < '0' || c > '9') && c !== '.') {
        isValid = false;
        break;
      }
    }
  } else {
    // If language is not recognized, default to "en" formatting
    s = s.replace(/,/g, '');

    // Check for invalid characters
    for (const c of s) {
      if ((c < '0' || c > '9') && c !== '.') {
        isValid = false;
        break;
      }
    }
  }

  // If the string is valid, parse the number
  if (isValid) {
    if (!isNaN(parseFloat(s))) {
      result = parseFloat(s);
    }
  }

  return result;
}

// Convert a string to a double
export function convertStringToDouble(value) {
  //if null or undefined, return 0
  if (value === null || value === undefined) return 0;

  value = value.toString();
  if (!value || value.trim() === '') return 0;
  const outVal = parseFloat(value);
  return isNaN(outVal) || !isFinite(outVal) ? 0 : outVal;
}

// Convert a string to an integer
export function convertStringToInt(value) {
  //if null or undefined, return 0
  if (value === null || value === undefined) return 0;

  try {
    value = value.toString();
  } catch {
    value = '';
  }

  if (!value || value.trim() === '') return 0;
  const outVal = parseInt(value, 10);
  return isNaN(outVal) ? 0 : outVal;
}

// Convert a string to a decimal
export function convertStringToDecimal(value) {
  //if null or undefined, return 0
  if (value === null || value === undefined) return 0;

  value = value.toString();
  if (!value || value.trim() === '') return 0;
  const result = parseFloat(value);
  return isNaN(result) ? 0 : Math.round(result * 100) / 100;
}

// Convert a string to a float
export function convertStringToFloat(value) {
  //if null or undefined, return 0
  if (value === null || value === undefined) return 0;

  value = value.toString();
  if (!value || value.trim() === '') return 0;
  const result = parseFloat(value);
  return isNaN(result) ? 0 : Math.round(result * 100) / 100;
}

// Convert a string to a long integer
export function convertStringToLong(value) {
  //if null or undefined, return 0
  if (value === null || value === undefined) return 0;

  value = value.toString();
  if (!value || value.trim() === '') return 0;
  const result = BigInt(value);
  return isNaN(result) ? 0n : result;
}

// Convert a string to a boolean
export function convertStringToBool(value) {
  value = value.toString();
  if (!value || value.trim() === '') return false;
  return value.toLowerCase() === 'true' || value === '1';
}

// Convert a string to a date
export function convertStringToDate(value) {
  value = value.toString();
  if (!value || value.trim() === '') return new Date(0);
  const result = new Date(value);
  return isNaN(result.getTime()) ? new Date(0) : result;
}

// Convert a string array to a single string with a divider
export function convertStringArrayToString(array, divider = '') {
  return Array.isArray(array) ? array.join(divider) : '';
}

// Convert binary image data to a base64 string
export function convertBinaryImageToString(value) {
  return value ? btoa(String.fromCharCode(...value)) : '';
}

// Convert a base64 string to bytes
export function convertToBytesFromBase64(base64String) {
  return base64String ? Uint8Array.from(atob(base64String), c => c.charCodeAt(0)) : [];
}

// Convert decimal to two decimal places
export function convertDecimalToTwoDecimalPlaces(number) {
  return Math.round(number * 100) / 100;
}

// Convert double to two decimal places
export function convertDoubleToTwoDecimalPlaces(number) {
  return Math.round(number * 100) / 100;
}

// Validate and convert a number string to double considering language-specific formatting
export function getMoneyInBasicUSDFormat(value, languageCode) {
  if (!value || value.trim() === '') return 0.0;
  value = value.replace(/\s/g, '').trim();
  if (['fr', 'es', 'de', 'pt', 'tr'].includes(languageCode)) {
    value = value.replace('.', '').replace(',', '.');
  } else {
    value = value.replace(',', '');
  }
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0.0 : parsed;
}

/**
 * Formats a Date according to a .NET‐style pattern and locale.
 *
 * @param {Date}   date        – the Date object to format
 * @param {string} dateFormat  – .NET‐style format, e.g. "dddd, MMMM dd"
 * @param {string} languageCode – BCP 47 language tag, e.g. "en" or "ar"
 * @returns {string} formatted date or "unknown date" on invalid input
 */
export function convertDateToStringAndFormatForLanguage(dateRaw, dateFormat, languageCode) {
  const date = new Date(dateRaw);

  if (
    !(date instanceof Date) ||
    isNaN(date) ||
    typeof dateFormat !== 'string' ||
    !languageCode.trim()
  ) {
    return 'unknown date';
  }

  // Arabic uses Lebanon variant for month/day names
  const locale = languageCode === 'ar' ? 'ar-LB' : languageCode;

  // Build Intl.DateTimeFormat options from .NET‐style tokens
  const opts = {};
  if (/dddd/.test(dateFormat)) opts.weekday = 'long';
  else if (/ddd/.test(dateFormat)) opts.weekday = 'short';

  if (/MMMM/.test(dateFormat)) opts.month = 'long';
  else if (/MMM/.test(dateFormat)) opts.month = 'short';
  else if (/MM/.test(dateFormat)) opts.month = '2-digit';
  else if (/M(?!M)/.test(dateFormat)) opts.month = 'numeric';

  if (/dd/.test(dateFormat)) opts.day = '2-digit';
  else if (/d(?!d)/.test(dateFormat)) opts.day = 'numeric';

  if (/yyyy/.test(dateFormat)) opts.year = 'numeric';
  else if (/yy/.test(dateFormat)) opts.year = '2-digit';

  return new Intl.DateTimeFormat(locale, opts).format(date);
}

//export function convertDateToStringAndFormatForLanguage(value, languageCode){

//    const date = new Date(item.dateCreated).toLocaleDateString(appState.SiteLanguageCookie, { month: 'long', day: '2-digit', year: 'numeric' });

//    //const estDaysX = convertStringToDouble(tempEstDeliveryDays);
//    //const today = new Date();
//    //today.setUTCDate(today.getUTCDate() + estDaysX);

//    //const locale = languageCode === "ar" ? "ar-LB" : languageCode;
//    //const options = { weekday: 'long', month: 'long', day: '2-digit' };
//    //const formatted = new Intl.DateTimeFormat(locale, options).format(today);

//}
